import { useEffect, useRef, type RefObject } from 'react';
import { VelocityTracker } from './velocity';

// Raw vertical drag tracking shared by BottomSheet, DraggableSheet and
// SwipeDismissLayer. Touch input uses touch events (they keep firing while the
// browser scrolls, unlike pointer events, and a non-passive touchmove can stop
// scrolling once our drag owns the gesture). Mouse and pen use pointer events.

export interface VerticalDragHandlers {
  /** Finger/button went down; stop any running animation. */
  onDown?: (target: EventTarget | null) => void;
  /** When true the slop is skipped (e.g. the sheet is already displaced). */
  skipSlop?: () => boolean;
  /** Decides once, after the slop, whether this gesture becomes a drag. dy > 0 is downward. */
  shouldStart: (dy: number, dx: number, target: EventTarget | null) => boolean;
  onStart?: () => void;
  /** Incremental vertical movement while dragging. */
  onMove: (deltaY: number) => void;
  /** Drag released; velocity in px/s, positive downward. */
  onEnd: (velocityY: number) => void;
  /** Gesture ended without becoming a drag. */
  onRelease?: () => void;
}

export interface VerticalDragOptions {
  enabled?: boolean;
  /** Movement before a drag can start (Flutter dragStartSlop 6, touch slop 18). */
  slop?: number;
}

interface GestureState {
  startX: number;
  startY: number;
  lastY: number;
  target: EventTarget | null;
  decided: boolean;
  dragging: boolean;
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function useVerticalDrag(
  ref: RefObject<HTMLElement | null>,
  handlers: VerticalDragHandlers,
  { enabled = true, slop = 6 }: VerticalDragOptions = {},
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;
    const tracker = new VelocityTracker();
    let gesture: GestureState | null = null;
    let touchId: number | null = null;
    let suppressClickUntil = 0;
    let restoreUserSelect: string | null = null;

    const begin = (x: number, y: number, target: EventTarget | null) => {
      gesture = { startX: x, startY: y, lastY: y, target, decided: false, dragging: false };
      tracker.reset();
      tracker.add(now(), y);
      handlersRef.current.onDown?.(target);
    };

    /** Returns true when the event should be prevented (we own the gesture). */
    const move = (x: number, y: number): boolean => {
      const g = gesture;
      if (!g) return false;
      const delta = y - g.lastY;
      g.lastY = y;
      tracker.add(now(), y);
      if (!g.decided) {
        const dy = y - g.startY;
        const dx = x - g.startX;
        const h = handlersRef.current;
        if (!h.skipSlop?.() && Math.abs(dy) < slop && Math.abs(dx) < slop) return false;
        g.decided = true;
        g.dragging = h.shouldStart(dy, dx, g.target);
        if (!g.dragging) return false;
        h.onStart?.();
      }
      if (!g.dragging) return false;
      handlersRef.current.onMove(delta);
      return true;
    };

    const end = () => {
      const g = gesture;
      gesture = null;
      if (restoreUserSelect !== null) {
        document.body.style.userSelect = restoreUserSelect;
        restoreUserSelect = null;
      }
      if (!g) return;
      if (g.dragging) {
        const releasedAt = now();
        suppressClickUntil = releasedAt + 80;
        handlersRef.current.onEnd(tracker.velocity(releasedAt));
      } else {
        handlersRef.current.onRelease?.();
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (touchId !== null) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      touchId = touch.identifier;
      begin(touch.clientX, touch.clientY, event.target);
    };
    const findTouch = (list: TouchList) => {
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t && t.identifier === touchId) return t;
      }
      return null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const touch = findTouch(event.changedTouches);
      if (!touch) return;
      if (move(touch.clientX, touch.clientY) && event.cancelable) event.preventDefault();
    };
    const onTouchEnd = (event: TouchEvent) => {
      if (!findTouch(event.changedTouches)) return;
      touchId = null;
      end();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (move(event.clientX, event.clientY)) {
        if (restoreUserSelect === null) {
          restoreUserSelect = document.body.style.userSelect;
          document.body.style.userSelect = 'none';
        }
      }
    };
    const detachWindow = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
    function onPointerUp() {
      detachWindow();
      end();
    }
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || event.button !== 0 || gesture) return;
      begin(event.clientX, event.clientY, event.target);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    };
    const onClickCapture = (event: MouseEvent) => {
      if (now() < suppressClickUntil) {
        event.stopPropagation();
        event.preventDefault();
      }
    };

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('touchcancel', onTouchEnd);
    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('click', onClickCapture, true);
    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', onTouchEnd);
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('click', onClickCapture, true);
      detachWindow();
      gesture = null;
      if (restoreUserSelect !== null) document.body.style.userSelect = restoreUserSelect;
    };
  }, [ref, enabled, slop]);
}
